import { waitFor } from '@/lib/helpers/utils';
import axios, { AxiosResponse } from 'axios';
import { AUTH_SESSION_QUERY_KEY } from '../queries/authSession';
import { queryClient } from '../redux/wrapWithProvider';
import { clearAuthCSRFTokenCache, getCachedAuthCSRFToken } from './authCSRFToken';
import { TokenRefreshResponse } from './authTypes';

/**
 * Fetches a fresh user-service JWT from the backend.
 *
 * Used only by Pathways.tsx for obtaining a JWT
 * to pass to an external plugin.
 *
 * @return {Promise<TokenRefreshResponse>}
 */
export const refreshToken = async (): Promise<TokenRefreshResponse> => {
  try {
    const response: AxiosResponse = await axios.post(
      '/api/refreshToken',
      {},
      {
        validateStatus: (status) => {
          return status == 200;
        }
      }
    );

    switch (response.status) {
      case 200: {
        const newToken = response.data.token;
        if (!newToken) {
          return { success: false, token: '', status: -1 };
        }
        return { success: true, token: newToken, status: 200 };
      }
    }
    return { success: false, token: '', status: -1 };
  } catch (err) {
    return { success: false, token: '', status: -2 };
  }
};

/**
 * Fetch the oauth user from session.
 *
 * @param {any} values to update user profile
 * @return {boolean | any}
 */
export const oauthUserFetch = async (type: string) => {
  try {
    const response = await axios.get(`/api/oauth/user/${type}`, {});
    if (response.status == 200 && response.data) {
      return { valid: true, data: response.data };
    }
    return { valid: false, data: null };
  } catch (err) {
    return false;
  }
};

/**
 * Makes a request to get a CSRF token.
 *
 * @param {string} path
 * @param {string} formId
 * @return {boolean}
 */
export const getCSRFToken = async (path: string, formId: string) => {
  try {
    const response = await axios.post(`/api/csrftoken`, {
      path: path,
      formId: formId
    });
    if (response.status != 200) {
      console.error('Unable to set token', path, formId);
      return false;
    }
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

/**
 * Makes a request to get a CSRF token.
 *
 * @return {Promise<string>}
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Server session expired — auth cache has been reset');
    this.name = 'SessionExpiredError';
  }
}

type AuthCSRFTokenOptions = {
  forceRefresh?: boolean;
};

const SESSION_TOKEN_MAX_ATTEMPTS = 3;
const SESSION_TOKEN_RETRY_DELAY_MS = 250;

export const getAuthCSRFToken = async (options: AuthCSRFTokenOptions = {}): Promise<string> => {
  for (let attempt = 1; attempt <= SESSION_TOKEN_MAX_ATTEMPTS; attempt++) {
    try {
      return await getCachedAuthCSRFToken(options);
    } catch (err) {
      const isSessionRejection =
        axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 401);

      if (!isSessionRejection) {
        console.error(err);
        return '';
      }

      // Straight after logging in or registering the new session cookie may not
      // have been applied yet, so a rejection here is not proof the session is
      // gone. Clear the cache and give it a moment before giving up on it.
      clearAuthCSRFTokenCache();

      if (attempt < SESSION_TOKEN_MAX_ATTEMPTS) {
        console.debug('CSRF token request rejected, retrying', { attempt });
        await waitFor(SESSION_TOKEN_RETRY_DELAY_MS * attempt);
        continue;
      }

      console.debug('CSRF token request rejected — session expired, resetting auth cache');
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, {
        isAuthenticated: false,
        authLevel: 'NONE',
        user: null,
        roles: [],
      });
      throw new SessionExpiredError();
    }
  }

  return '';
};
